import { useCallback, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { H7 } from 'foundations-components/transitional/components/H7';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { View } from 'foundations-components/transitional/components/View';
import { HR } from 'foundations-components/transitional/components/HR';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { ChirpError } from 'rpc-client-utils';
import { useLoginVerify } from '../hooks/useLoginVerify';
import { useUpdateUserSettingsMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/SettingsRpc.hooks';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { usePortalsPlatformObjectId } from '../context/PortalsPlatformObjectIdContext';
import { Flex } from 'foundations-components/components/Flex';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { useIsUngated } from '../hooks/useIsUngated';
import { PasswordResetPanel } from './PasswordResetPanel';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useProfilePhotoUpload } from '../hooks/useProfilePhotoUpload';
import { ProfilePhotoSection } from './ProfilePhotoSection';
import { AccountAndProfileSection } from './AccountAndProfileSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PANEL_WIDTH_BY_BREAKPOINT = {
  mobile: '100%',
  tablet: '80%',
  desktop: 600
};
const PROFILE_PHOTO_UPLOAD_GATE = 'PortalsPlatform::Settings::ProfilePhotoUpload';
const RESET_PASSWORD_GATE = 'PortalsPlatform::Settings::ResetPassword';
const PREFERRED_LANGUAGE_GATE = 'PortalsPlatform::SupportPreferredLanguage';
function SettingsForm({
  onOpenChangePassword,
  alertStore
}) {
  const {
    data: loginVerifyData,
    loading,
    refetch: refetchLoginVerify
  } = useLoginVerify();
  const isProfilePhotoUploadEnabled = useIsUngated(PROFILE_PHOTO_UPLOAD_GATE);
  const isResetPasswordEnabled = useIsUngated(RESET_PASSWORD_GATE);
  const isPreferredLanguageEnabled = useIsUngated(PREFERRED_LANGUAGE_GATE);
  const photo = useProfilePhotoUpload(loginVerifyData, refetchLoginVerify, alertStore);
  const [saving, setSaving] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState();
  const [languageError, setLanguageError] = useState();
  const displayLanguage = pendingLanguage !== null && pendingLanguage !== void 0 ? pendingLanguage : loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.preferredLanguage;
  const hasLanguageChanged = pendingLanguage !== undefined;
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const objectId = usePortalsPlatformObjectId();
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const handleSaveChanges = useCallback(async () => {
    setSaving(true);
    setLanguageError(undefined);
    try {
      const photoSaved = await photo.save();
      if (!photoSaved) {
        return;
      }
      let hasError = false;
      if (isPreferredLanguageEnabled && hasLanguageChanged && pendingLanguage) {
        try {
          await updateUserSettings({
            variables: {
              preferredLanguage: pendingLanguage,
              accountId,
              portalInstanceId,
              objectId
            }
          });
          await refetchLoginVerify();
          setPendingLanguage(undefined);
        } catch (error) {
          var _networkError$cause, _networkError;
          hasError = true;
          const rpcError = (_networkError$cause = error === null || error === void 0 || (_networkError = error.networkError) === null || _networkError === void 0 ? void 0 : _networkError.cause) !== null && _networkError$cause !== void 0 ? _networkError$cause : error === null || error === void 0 ? void 0 : error.cause;
          const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
          if ((settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) === 'INVALID_PREFERRED_LANGUAGE') {
            setLanguageError(unescapedText('app.myAccountPopover.settings.profile.errorInvalidLanguage'));
          } else {
            setLanguageError(unescapedText('app.myAccountPopover.settings.profile.errorSaveFailed'));
          }
        }
      }
      if (!hasError) {
        alertStore.addAlert({
          type: 'success',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.saveChangesSuccess"
          }),
          dismissAutoMs: 5000
        });
      }
    } finally {
      setSaving(false);
    }
  }, [photo, isPreferredLanguageEnabled, hasLanguageChanged, pendingLanguage, updateUserSettings, accountId, portalInstanceId, objectId, refetchLoginVerify, alertStore]);
  return /*#__PURE__*/_jsxs(View, {
    children: [isProfilePhotoUploadEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProfilePhotoSection, {
        loginVerifyData: loginVerifyData,
        photo: photo,
        loading: loading
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), /*#__PURE__*/_jsx(AccountAndProfileSection, {
      loginVerifyData: loginVerifyData,
      loading: loading,
      isPreferredLanguageEnabled: isPreferredLanguageEnabled,
      displayLanguage: displayLanguage,
      languageError: languageError,
      onLanguageChange: language => {
        setPendingLanguage(language);
        setLanguageError(undefined);
      }
    }), !loginVerifyData && !loading && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "center",
        gap: "200",
        paddingBlock: "500",
        role: "alert",
        children: [/*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "heading-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.error.title"
          })
        }), /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.error.description"
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), isResetPasswordEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        marginBlockStart: "300",
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(H5, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.security.title"
          })
        }), /*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.security.changePasswordLabel'),
              children: /*#__PURE__*/_jsx(Flex, {
                inlineSize: "100%",
                children: /*#__PURE__*/_jsx(Button, {
                  use: "unstyled",
                  onClick: onOpenChangePassword,
                  flex: "auto",
                  children: /*#__PURE__*/_jsx(View, {
                    inlineSize: "100%",
                    borderWidth: "100",
                    borderStyle: "solid",
                    borderColor: "border-core-default",
                    borderRadius: "200",
                    backgroundColor: "fill-surface-default",
                    paddingInline: "200",
                    paddingBlock: "100",
                    children: /*#__PURE__*/_jsxs(Flex, {
                      alignItems: "center",
                      justifyContent: "space-between",
                      inlineSize: "100%",
                      gap: "200",
                      children: [/*#__PURE__*/_jsxs(Flex, {
                        alignItems: "center",
                        gap: "200",
                        children: [/*#__PURE__*/_jsx(LockedIcon, {
                          size: "300",
                          color: "text-core-default"
                        }), /*#__PURE__*/_jsx(Paragraph, {
                          color: "text-core-default",
                          marginBlock: "0",
                          typography: "body-100",
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "app.myAccountPopover.settings.security.changePassword"
                          })
                        })]
                      }), /*#__PURE__*/_jsx(RightIcon, {
                        size: "300",
                        color: "text-core-default"
                      })]
                    })
                  })
                })
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), (isProfilePhotoUploadEnabled || isPreferredLanguageEnabled) && /*#__PURE__*/_jsx(LoadingButton, {
      use: "primary",
      block: true,
      marginBlockStart: "400",
      onClick: () => void handleSaveChanges(),
      loading: saving,
      disabled: saving,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.settings.saveChanges"
      })
    })]
  });
}
export function SettingsPanel({
  onClose
}) {
  const breakpoint = useBreakpoint();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [alertStore] = useState(() => FloatingAlertStore.newFloatingAlertStore());
  const isMobile = breakpoint === 'mobile';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Panel, {
      width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
      children: isMobile ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          position: "sticky",
          insetBlockStart: "0",
          zIndex: "1",
          backgroundColor: "fill-surface-default",
          borderBlockEndWidth: "100",
          borderBlockEndStyle: "solid",
          borderBlockEndColor: "border-core-default",
          paddingInline: "400",
          paddingBlock: "300",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "200",
          children: [/*#__PURE__*/_jsx(H4, {
            color: "text-core-default",
            marginBlock: "0",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.myAccountPopover.settings.heading"
            })
          }), /*#__PURE__*/_jsx(CloseButton, {
            inline: true,
            onClick: onClose,
            color: "text-core-default"
          })]
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(FloatingAlertList, {
              alertStore: alertStore,
              use: "contextual"
            }), /*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              onOpenChangePassword: () => setShowChangePassword(true),
              alertStore: alertStore
            })]
          })
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CloseButton, {
          onClick: onClose,
          color: "text-core-default"
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(FloatingAlertList, {
              alertStore: alertStore,
              use: "contextual"
            }), /*#__PURE__*/_jsx(H4, {
              color: "text-core-default",
              marginBlockEnd: "200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.heading"
              })
            }), /*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              onOpenChangePassword: () => setShowChangePassword(true),
              alertStore: alertStore
            })]
          })
        })]
      })
    }), showChangePassword && /*#__PURE__*/_jsx(PasswordResetPanel, {
      onClose: () => setShowChangePassword(false)
    })]
  });
}